import axios from "axios";

import { getUserCredentials } from "../utils/common";
import { API_URL } from "../utils/Constant";

const normalizeRequestType = (type) => (
  type?.toLowerCase() === "recurring" ? "recurring" : "normal"
);

/**
 * Prefer the FedEx API error message when present.
 * @param {Object} data response body
 * @returns {String|undefined} FedEx error message
 */
const getFedexErrorMessage = (data) => {
  const fedexErrors = data?.fedex_response?.errors;
  if (!Array.isArray(fedexErrors)) return undefined;
  return fedexErrors.map((err) => err?.message).filter(Boolean).join(" ") || undefined;
};

/**
 * FedEx errors are the most actionable, but fall back to the backend's own
 * message so non-FedEx failures aren't reported as a generic error.
 * @param {Object} data response body
 * @returns {String} error message
 */
const getPickupErrorMessage = (data) => (
  getFedexErrorMessage(data) || data?.message || "Something went wrong."
);

export const fetchSupplyRequests = async (params, signal = null) => {
  try {
    const resp = await axios.get(`${API_URL}/supply-request/`, {
      params,
      signal,
    });
    return { success: true, ...resp.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong.", canceled: e.code === "ERR_CANCELED" };
  }
};

export const fetchRecurringRequests = async (params, signal = null) => {
  try {
    const resp = await axios.get(`${API_URL}/recurring-request/`, {
      params,
      signal,
    });
    return { success: true, ...resp.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong.", canceled: e.code === "ERR_CANCELED" };
  }
};

export const fetchSupplyRequestDetail = async (supply_order_id, signal = null) => {
  try {
    const resp = await axios.get(`${API_URL}/supply-request/${supply_order_id}/`, {
      signal,
    });
    return { success: true, ...resp.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong.", canceled: e.code === "ERR_CANCELED" };
  }
};

export const createSupplyRequest = async (payload) => {
  try {
    payload.request_source = "esv";
    payload.request_type = "normal";
    const resp = await axios.post(`${API_URL}/supply-request/`, payload);
    return { success: true, ...resp.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong.", canceled: e.code === "ERR_CANCELED" };
  }
};

export const editSupplyRequest = async (payload) => {
  try {
    const userInfo = getUserCredentials();
    payload.request_source = "esv";
    payload.request_type = normalizeRequestType(payload.request_type);
    payload.updated_by = userInfo?.uid === "support" ? "" : userInfo?.user_name || "";
    const resp = await axios.patch(`${API_URL}/supply-request/`, payload);
    return { success: true, ...resp.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong.", canceled: e.code === "ERR_CANCELED" };
  }
};

export const createEditSupplyRecurrenceRequest = async (payload) => {
  try {
    payload.request_source = "esv";
    payload.request_type = "recurring";
    const resp = await axios.post(`${API_URL}/supply-request/`, payload);
    return { success: true, ...resp.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong.", canceled: e.code === "ERR_CANCELED" };
  }
};

export const editSupplyRecurrenceRequest = async (payload) => {
  try {
    const userInfo = getUserCredentials();
    payload.request_source = "esv";
    payload.request_type = "recurring";
    payload.updated_by = userInfo?.uid === "support" ? "" : userInfo?.user_name || "";
    const resp = await axios.patch(`${API_URL}/supply-request/`, payload);
    return { success: true, ...resp.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong.", canceled: e.code === "ERR_CANCELED" };
  }
};

export const getInventoryItems = async (search = "", signal = null, customer_id) => {
  try {
    const resp = await axios.get(`${API_URL}/inventory-management/`, {
      params: {
        search,
        is_deleted: 0,
        customer_id,
      },
      signal,
    });
    return { success: true, result: resp.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong.", canceled: e.code === "ERR_CANCELED" };
  }
};

export const fetchPickupRequests = async (params, signal = null) => {
  try {
    const resp = await axios.get(`${API_URL}/pickup-request/`, {
      params,
      signal,
    });
    return { success: true, ...resp.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong.", canceled: e.code === "ERR_CANCELED" };
  }
};

export const fetchPickupRequestDetail = async (pickup_order_id, signal = null) => {
  try {
    const resp = await axios.get(`${API_URL}/pickup-request/`, {
      params: {
        pickup_order_id,
        status: "",
        pickup_category: "",
        date_from: "",
        date_to: "",
        page: 1,
      },
      signal,
    });
    const [result] = resp.data?.result || [];
    return { success: true, ...resp.data, result };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong.", canceled: e.code === "ERR_CANCELED" };
  }
};

export const createPickupRequest = async (payload) => {
  try {
    payload.internal.request_source = "esv";
    const resp = await axios.post(`${API_URL}/pickup-request/`, payload);
    if (resp.data?.error || resp.data?.fedex_response?.errors) {
      return { success: false, message: getPickupErrorMessage(resp.data) };
    }
    return { success: true, ...resp.data };
  } catch (e) {
    return { success: false, message: getPickupErrorMessage(e.response?.data), canceled: e.code === "ERR_CANCELED" };
  }
};

export const editPickupRequest = async (payload) => {
  try {
    const userInfo = getUserCredentials();
    const updated_by = userInfo.uid === "support" ? "" : userInfo.user_name;
    payload.internal.edit_source = "esv";
    payload.internal.updated_by = updated_by;
    if (payload.internal.status !== "cancelled") {
      payload.internal.request_source = "esv";
    }
    const resp = await axios.patch(`${API_URL}/pickup-request/`, payload);
    if (resp.data?.error || resp.data?.fedex_response?.errors) {
      return { success: false, message: getPickupErrorMessage(resp.data) };
    }
    return { success: true, ...resp.data };
  } catch (e) {
    return { success: false, message: getPickupErrorMessage(e.response?.data), canceled: e.code === "ERR_CANCELED" };
  }
};

export const checkFedexPickupAvailability = async (payload) => {
  try {
    const resp = await axios.post(`${API_URL}/fedex-pickup-availability/`, payload);
    return { success: true, ...resp.data };
  } catch (e) {
    return {
      success: false,
      message: getPickupErrorMessage(e.response?.data),
      canceled: e.code === "ERR_CANCELED",
    };
  }
};
